import to from "await-to-js";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "../../../../components/Input/Input";
import { Table } from "../../../../components/Table/Table";
import style from "./UsedStampsList.module.scss";

import { authService } from "../../../../modules/auth/auth.service";

import { InfoCircleOutlined, MinusCircleTwoTone } from "@ant-design/icons";
import { Flex, Space, Tooltip } from "antd";
import { Button } from "../../../../components/Button/Button";
import { PopconfirmButton } from "../../../../components/PopconfirmButton/PopconfirmButton";
import { Select } from "../../../../components/Select/Select";
import { ModalsContext } from "../../../../context/modals.context";
import { usedStampsService } from "../../../../modules/obligee/usedStamps/usedStamps.service";
import {
  GetListaIskoriscenihAMPayload,
  IskoriscenaAM,
  KategorijaEvidentiraneMarkice,
  KategorijeEvidentiranja,
} from "../../../../modules/obligee/usedStamps/usedStamps.types";
import { formatDateDDMMYY } from "../../../../utils/dateFormatter";
import { mapSelectOptions } from "../../../../utils/map4select";
import { toastService } from "../../../../utils/toast.service";
import { UsedStampsExcelExportButton } from "./UsedStampsExcelExportButton";
import { UsedStampsPrometModal } from "./components/UsedStampsPrometModal/UsedStampsPrometModal";

const INITIAL_PARAMS: () => GetListaIskoriscenihAMPayload = () => ({
  kategorijaEvidentiranihMarkicaId:
    KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
  brojMarkice: null,
  brojZahteva: null,
  uPrometu: null,
});

export const UsedStampsListPage = () => {
  const [puObrasci, setUsedStamps] = useState<IskoriscenaAM[]>([]);
  const [params, setParams] = useState<GetListaIskoriscenihAMPayload>(
    INITIAL_PARAMS()
  );
  const [brojMarkice, setBrojMarkice] = useState<string | null>(null);
  const [brojZahteva, setBrojZahteva] = useState<string | null>(null);
  const [kategorijeEvidentiranja, setKategorijeEvidentiranja] = useState<
    KategorijeEvidentiranja[]
  >([]);

  const { t } = useTranslation();
  const { handleUpdateModals, setError } = useContext(ModalsContext);

  const updateParams = (
    accessor: keyof GetListaIskoriscenihAMPayload,
    value: string | null | number
  ) => {
    setParams({ ...params, [accessor]: value });
  };

  const updateMultipleParams = (
    params: Partial<GetListaIskoriscenihAMPayload>
  ) => {
    setParams((prev) => ({ ...prev, ...params }));
  };

  const getUsedStamps = async (filters?: GetListaIskoriscenihAMPayload) => {
    const [err, res] = await to(
      usedStampsService.getListaIskoriscenihAkciznihMarkica(filters ?? params)
    );

    if (err || !res) return;

    if (!res?.data && !res?.success && res?.errorMessage) {
      setError(res?.errorMessage);
      getUsedStamps(INITIAL_PARAMS());
      return;
    }

    setUsedStamps(res.data.evidentiraneMarkice ?? []);
    setKategorijeEvidentiranja(res.data.kategorijeEvidentiranja ?? []);
  };

  const handleIzbaciIzPrometa = async (opsegId: number | null) => {
    const [err, res] = await to(
      usedStampsService.postStavljanjeMarkicaUPromet({
        opsegId: opsegId,
        stavljanjeUPromet: false,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    getUsedStamps();
  };

  const handlePonistiOpseg = async (evidentiraniZapis: IskoriscenaAM) => {
    if (!evidentiraniZapis.opsegId) return;
    const [err, res] = await to(
      usedStampsService.postPonistiEvidentiraniOpseg({
        kategorijaEvidentiranihMarkicaId:
          params.kategorijaEvidentiranihMarkicaId!,
        opsegId: evidentiraniZapis.opsegId,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    getUsedStamps();
    toastService.success(t("TOAST.PONISTI_OPSEG_SUCCESS"));
  };

  const OBLIGEE_COL_DEF = [
    {
      title: t("EVIDENCIJA_MARKICA.BROJ_ZAM_ZAHTEVA"),
      dataIndex: "brojZahteva",
      width: "150px",
      isVisible: true,
    },
    {
      title: t("EVIDENCIJA_MARKICA.OZNAKA_VRSTE_MARKICE"),
      dataIndex: "markicaKategorijeVrste",
      width: "100px",
      isVisible: true,
    },
    {
      title: t("AKCIZNE_MARKICE.PERIOD"),
      dataIndex: "period",
      width: "150px",
      isVisible: [
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.OPSEG_SERIJSKIH_BROJEVA"),
      dataIndex: "opsegEvidentiranihMarkica",
      width: "200px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.KOLICINA"),
      dataIndex: "kolicina",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
    },
    {
      title: `${t("EVIDENCIJA_MARKICA.ZAPREMINA_AMBALAZE")} (L) /
                  ${t("EVIDENCIJA_MARKICA.KOLICINA")} (kg) ${t(
        "EVIDENCIJA_MARKICA.PO_POJEDINACNOM_PAKOVAJNU"
      )}`,
      dataIndex: "ambalaza",
      width: "200px",
      isVisible: [KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ].includes(
        params.kategorijaEvidentiranihMarkicaId
      ),
    },
    {
      title: t("EVIDENCIJA_MARKICA.KOLICINA"),
      dataIndex: "kolicinaOstecenihVidljivaSerija",
      width: "120px",
      isVisible: [KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE].includes(
        params.kategorijaEvidentiranihMarkicaId
      ),
    },
    {
      title: t("EVIDENCIJA_MARKICA.DATUM_EVIDENTIRANJA"),
      dataIndex: "datumEvidentiranja",
      isVisible: [
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
      render: (datum: string) => formatDateDDMMYY(datum),
    },
    {
      title: t("EVIDENCIJA_MARKICA.DATUM_PROIZVODNJE_ODNOSNO_CARINJENJA"),
      dataIndex: "opsegDatumaProizvodnje",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.LOKACIJA"),
      dataIndex: "lokacija",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.MASINA"),
      dataIndex: "masina",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.BREND"),
      dataIndex: "brend",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
    },
    {
      title: (
        <Tooltip
          title={t("EVIDENCIJA_MARKICA.STAVLJENO_U_PROMET_PRODAJU_INFO")}
        >
          {t("EVIDENCIJA_MARKICA.STAVLJENO_U_PROMET_PRODAJU")}&nbsp;
          <InfoCircleOutlined />
        </Tooltip>
      ),
      width: 400,
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
      ].includes(params.kategorijaEvidentiranihMarkicaId),
      render: (record: IskoriscenaAM) => (
        <Flex gap={16} align="center">
          <strong style={{ fontFamily: "unset" }}>
            {record.opseziUPrometuInfo?.kolicina}
          </strong>
          <Flex gap={10} vertical>
            {record.opseziUPrometuInfo?.opsezi?.map((opseg, i) => (
              <Flex
                className={style.opseziUPrometuCell}
                gap={8}
                align="center"
                wrap="wrap"
              >
                <Flex align="end" gap={4}>
                  {opseg?.opseg && (
                    <>
                      <span style={{ fontFamily: "unset", color: "#1890ff" }}>
                        {opseg?.opseg}
                      </span>
                      <span>&nbsp;-&nbsp;</span>
                    </>
                  )}
                  <span style={{ fontFamily: "unset" }}>{opseg?.kolicina}</span>
                  {opseg?.ambalaza ? (
                    <span
                      style={{ fontSize: 12 }}
                    >{`(${opseg?.ambalaza})`}</span>
                  ) : (
                    ""
                  )}
                </Flex>
                <span>-</span>
                <span>{opseg.datumStavljanjaUPromet}</span>
                <span>
                  <PopconfirmButton
                    buttonName={`delete-opseg-promet-${i}`}
                    title={t("POPCONFIRM.IZBACI_IZ_PROMETA")}
                    onConfirm={() => handleIzbaciIzPrometa(opseg.opsegPrometId)}
                  >
                    <MinusCircleTwoTone twoToneColor="red" />
                  </PopconfirmButton>
                </span>
              </Flex>
            ))}
          </Flex>
        </Flex>
      ),
    },
    {
      title: t("COMMON.AKCIJE"),
      isVisible: true,
      width: [KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA].includes(
        params.kategorijaEvidentiranihMarkicaId
      )
        ? "250px"
        : "120px",
      fixed: "right" as any,
      render: (record: IskoriscenaAM) => {
        return (
          <Flex align="center" gap={8}>
            <PopconfirmButton
              buttonName={`delete-evidenciju-${record.opsegId}`}
              buttonText={t("EVIDENCIJA_MARKICA.PONISTI")}
              buttonType="primary"
              disabled={record.obradjen}
              title={t("POPCONFIRM.PONISTI_OPSEG")}
              onConfirm={() => handlePonistiOpseg(record)}
            ></PopconfirmButton>
            {[KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA].includes(
              params.kategorijaEvidentiranihMarkicaId
            ) && (
              <Button
                type="primary"
                onClick={() =>
                  handleUpdateModals({
                    modalVisible: "stavljanjeMarkicaUPromet",
                    iskoriscenOpsegId: record.opsegId,
                    markicaKategorijeId: record.markicaKategorijeId,
                    isPosebniPostupak: record.isPosebniPostupak,
                  })
                }
              >
                {record.isPosebniPostupak
                  ? t("EVIDENCIJA_MARKICA.PRODAJA")
                  : t("EVIDENCIJA_MARKICA.STAVI_U_PROMET")}
              </Button>
            )}
          </Flex>
        );
      },
    },
  ];

  const MINISTRY_COL_DEF = [
    {
      title: t("PU_OBRASCI.PIB"),
      dataIndex: "pib",
      width: "120px",
      isVisible: true,
    },
    ...OBLIGEE_COL_DEF,
  ];

  const ROLE_COL_DEF = authService
    .getRole()
    .some((role) => role.includes("MFIN"))
    ? MINISTRY_COL_DEF
    : OBLIGEE_COL_DEF;

  const VISIBLE_COL_DEF = ROLE_COL_DEF.filter((col) => col.isVisible);

  const U_PROMETU_OPTIONS = [
    {
      label: t("EVIDENCIJA_MARKICA.FILTER_U_PROMETU"),
      value: true,
    },
    {
      label: t("EVIDENCIJA_MARKICA.FILTER_VAN_PROMETA"),
      value: false,
    },
    {
      label: t("EVIDENCIJA_MARKICA.FILTER_SVE"),
      value: null,
    },
  ];

  useEffect(() => {
    getUsedStamps();
  }, [params]);

  return (
    <div className={style.mainContainer}>
      <UsedStampsPrometModal getUsedStamps={getUsedStamps} />
      <Flex
        gap={12}
        id="ministryLicencesFilters"
        className={style.actionsContainer}
      >
        <Flex flex={1 / 3}>
          <Select
            placeholder={t("PLACEHOLDERS.KATEGORIJE_EVIDENTIRANJA")}
            options={mapSelectOptions({
              labelAccessor: "name",
              valueAccessor: "id",
              sourceArray: kategorijeEvidentiranja,
            })}
            onChange={(value) => {
              if (
                value !== KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA
              ) {
                // clear uPrometuValue since its not applicable to anu other type than KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA
                updateMultipleParams({
                  kategorijaEvidentiranihMarkicaId: value,
                  uPrometu: null,
                });
              } else {
                updateParams("kategorijaEvidentiranihMarkicaId", value);
              }
            }}
            value={params.kategorijaEvidentiranihMarkicaId}
            name="kategorije"
          />
        </Flex>

        <Flex gap={12} flex={2 / 3}>
          {[KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA].includes(
            params.kategorijaEvidentiranihMarkicaId
          ) && (
            <Select
              allowClear
              onChange={(value) => updateParams("uPrometu", value)}
              placeholder={t("PLACEHOLDERS.STATUS")}
              options={U_PROMETU_OPTIONS as any}
              defaultValue={null}
              name="u-prometu"
            />
          )}
          {[
            KategorijaEvidentiraneMarkice.NEISKORISCENE,
            KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
            KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
            KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
          ].includes(params.kategorijaEvidentiranihMarkicaId) && (
            <Space.Compact style={{ width: "100%" }}>
              <Input
                allowClear
                onChange={({ target }) => {
                  setBrojMarkice(target.value);
                }}
                placeholder={t("PLACEHOLDERS.BROJ_MARKICE")}
                name="filter-broj-markice"
              />
              <Button
                type="primary"
                onClick={() => {
                  updateParams("brojMarkice", brojMarkice);
                }}
              >
                {t("COMMON.PRETRAZI")}
              </Button>
            </Space.Compact>
          )}

          <Space.Compact style={{ width: "100%" }}>
            <Input
              onChange={({ target }) => {
                setBrojZahteva(target.value);
              }}
              placeholder={t("EVIDENCIJA_MARKICA.BROJ_ZAM_ZAHTEVA")}
              name="filter-zam-broj"
              style={{
                width: [
                  KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
                ].includes(params.kategorijaEvidentiranihMarkicaId)
                  ? "50%"
                  : "100%",
              }}
              allowClear
            />
            <Button
              type="primary"
              onClick={() => {
                updateParams("brojZahteva", brojZahteva);
              }}
            >
              {t("COMMON.PRETRAZI")}
            </Button>
          </Space.Compact>
        </Flex>
        <UsedStampsExcelExportButton
          evidentiraneMarkice={puObrasci}
          kategorijaEvidentiranihMarkicaId={
            params.kategorijaEvidentiranihMarkicaId
          }
        />
      </Flex>
      <Table
        columns={VISIBLE_COL_DEF}
        scrollConfig={["#ministryLicencesFilters", "#ministryLicencesTitle"]}
        rowClassName="pointer"
        dataSource={puObrasci}
        rowKey="opsegIskoriscenihMarkica"
        name="mfin-dozvole-tabela"
      />
    </div>
  );
};
